import { Button, Flex, Group, Modal, Text } from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { ApiError, ApiErrorCode } from 'src/error/ApiError';
import { z } from 'zod';
import ModalBackButton from 'src/UI/common/ModalBackButton';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import { User, USER_ROLES } from '../types';
import useUsers from '../hooks/useUsers';
import { useUsersContext, CreateUserData } from '../context/UsersContext';
import { GenericErrorModal } from 'src/error/GenericErrorModal';
import PageLoader from 'src/UI/loader/PageLoader';
import CreateUserForm, { CreateUserFormValues } from './CreateUserForm';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useState } from 'react';

type CreateUserModalProps = {
  opened: boolean;
  close: () => void;
  canGoBack?: boolean;
  previousStep?: () => void;
  createUserOverride?: (userData: CreateUserData) => Promise<unknown>;
  isLoading?: boolean;
  showSecretMenuAccessOption: boolean;
};

export function isDuplicateEmail(users: User[], emailAddress: string) {
  return users.some(user => user.emailAddress?.toLowerCase() === emailAddress.toLowerCase());
}

export const formSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  emailAddress: z.string().email('Please enter a valid email address'),
  userRole: z.enum(USER_ROLES),
});

export default function CreateUserModal({
  opened,
  close,
  canGoBack,
  previousStep,
  createUserOverride,
  isLoading: isLoadingOverride = false,
  showSecretMenuAccessOption = false,
}: CreateUserModalProps) {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { createUser, error, isLoading } = useUsersContext();
  const { users } = useUsers();

  const form = useForm<CreateUserFormValues>({
    mode: 'controlled',
    initialValues: {
      firstName: '',
      lastName: '',
      emailAddress: '',
      userRole: 'ADVISOR',
    },
    validate: values => {
      const zodErrors = schemaResolver(formSchema, { sync: true })(values);

      if (!zodErrors.emailAddress && isDuplicateEmail(users, values.emailAddress)) {
        zodErrors.emailAddress = 'This email address is already in use.';
      }

      return zodErrors;
    },
    validateInputOnBlur: true,
  });

  const onCreateUser = async (values: CreateUserFormValues) => {
    try {
      const user = { ...values, userStatus: 'PENDING' } as CreateUserData;
      const createUserFunction = createUserOverride || createUser;
      await createUserFunction(user);
      Mixpanel.track(MixpanelEvents.USER_CREATED);
      setShowSuccessModal(true);
      form.reset();
    } catch (e) {
      if (ApiError.isApiError(ApiErrorCode.DUPLICATE_EMAIL, e)) {
        form.setFieldError('emailAddress', e.message);
      }
      notifyAndLogError(
        'Failed to create user',
        'An error occurred while creating the user. Please try again.',
        e,
        { userData: values },
      );
    }
  };

  if (error instanceof ApiError && !ApiError.isApiError(ApiErrorCode.DUPLICATE_EMAIL, error)) {
    return <GenericErrorModal opened={true} onClose={close} />;
  }

  return (
    <>
      <Modal opened={opened} onClose={close} title="Invite new user" centered>
        {previousStep && (
          <ModalBackButton previousStep={previousStep} canGoBack={Boolean(canGoBack)} />
        )}
        {isLoading || isLoadingOverride ? (
          <PageLoader absolute={false} />
        ) : (
          <form onSubmit={form.onSubmit(values => onCreateUser(values))}>
            <Flex direction="column" gap="md">
              <CreateUserForm form={form} showSecretMenuAccessOption={showSecretMenuAccessOption} />
            </Flex>

            <Group justify="flex-end" mt="md">
              <Button type="submit" variant="filled-pill-black" disabled={!form.isValid()}>
                Add
              </Button>
            </Group>
          </form>
        )}
      </Modal>
      <Modal
        opened={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          close();
        }}
        title="User created"
        centered
      >
        <Text>User was successfully created.</Text>
        <Group justify="flex-end" mt="md">
          <Button
            onClick={() => {
              setShowSuccessModal(false);
              close();
            }}
          >
            OK
          </Button>
        </Group>
      </Modal>
    </>
  );
}
